import { type CsvTarget, type ParsedCsv } from "./csv";
import { krigeHaney, type HaneyKrigePoint } from "./haneyKrigeClient";
import { stackCellData } from "./stackClient";
import type { HaneySamplePoint, ManifestLayer } from "../types/contract";

// The Haney H3A panel parameters we ingest, krige, and display. `key` doubles as the layer id
// suffix (`haney-<key>`) and the stored value key. Aliases cover the common lab-export headers.
export interface HaneyParam extends CsvTarget {
  unit: string;
}
export const HANEY_PARAMS: HaneyParam[] = [
  {
    key: "soil_health_score",
    label: "Soil health score",
    unit: "",
    aliases: ["soilhealthscore", "soilhealthcalculation", "healthscore", "shc"],
  },
  {
    key: "co2c",
    label: "Soil respiration (CO₂-C)",
    unit: "ppm",
    aliases: [
      "co2c",
      "co2cppm",
      "1dayco2c",
      "co2cppm1day",
      "soilrespiration",
      "respiration",
    ],
  },
  {
    key: "weoc",
    label: "Water-extractable organic C (WEOC)",
    unit: "ppm",
    aliases: ["weoc", "weocppm", "waterextractableorganiccarbon", "wexoc"],
  },
  {
    key: "weon",
    label: "Water-extractable organic N (WEON)",
    unit: "ppm",
    aliases: ["weon", "weonppm", "waterextractableorganicnitrogen", "wexon"],
  },
  {
    key: "weoc_weon",
    label: "Organic C:N (WEOC:WEON)",
    unit: "",
    aliases: [
      "weocweon",
      "weocweonratio",
      "organiccn",
      "cnratio",
      "weoctoweon",
    ],
  },
  {
    key: "inorganic_n",
    label: "Inorganic N",
    unit: "ppm",
    aliases: ["inorganicn", "inorganicnppm", "inorgn", "h3ainorganicn"],
  },
  {
    key: "inorganic_p",
    label: "Inorganic P",
    unit: "ppm",
    aliases: ["inorganicp", "inorganicpppm", "inorgp", "h3ap"],
  },
  {
    key: "total_p",
    label: "Total P",
    unit: "ppm",
    aliases: ["totalp", "totalpppm", "totp"],
  },
  {
    key: "organic_p",
    label: "Organic P",
    unit: "ppm",
    aliases: ["organicp", "organicpppm", "orgp"],
  },
  {
    key: "h3a_k",
    label: "Potassium (H3A)",
    unit: "ppm",
    aliases: ["h3ak", "h3akppm", "potassiumh3a"],
  },
  {
    key: "mac_pct",
    label: "Microbially active C",
    unit: "%",
    aliases: ["macpct", "mac", "microbiallyactivecarbon", "pctmac"],
  },
  { key: "ph", label: "pH", unit: "", aliases: ["ph", "soilph"] },
  {
    key: "available_n_lb_ac",
    label: "Available N",
    unit: "lb/ac",
    aliases: ["availablenlbac", "availn", "availablen"],
  },
  {
    key: "available_p_lb_ac",
    label: "Available P",
    unit: "lb/ac",
    aliases: ["availableplbac", "availp"],
  },
  {
    key: "available_k_lb_ac",
    label: "Available K",
    unit: "lb/ac",
    aliases: ["availableklbac", "availk", "availablek"],
  },
];

// Location + identity targets (mirrors lib/csv) plus the Haney parameter targets.
export const HANEY_TARGETS: CsvTarget[] = [
  {
    key: "lat",
    label: "Latitude",
    required: true,
    aliases: ["lat", "latitude", "y", "pointy", "latdd", "gpslat"],
  },
  {
    key: "lon",
    label: "Longitude",
    required: true,
    aliases: [
      "lon",
      "lng",
      "long",
      "longitude",
      "x",
      "pointx",
      "londd",
      "gpslon",
      "gpslong",
    ],
  },
  {
    key: "id",
    label: "Sample ID",
    aliases: [
      "id",
      "sampleid",
      "sampleno",
      "samplenumber",
      "pointid",
      "label",
      "name",
    ],
  },
  {
    key: "date",
    label: "Sample date",
    aliases: ["date", "sampledate", "reportdate", "sampledat"],
  },
  {
    key: "depth_in",
    label: "Depth (in)",
    aliases: ["depth", "depthin", "depthinches", "sampledepth"],
  },
  ...HANEY_PARAMS,
];
const PARAM_KEYS = new Set(HANEY_PARAMS.map((p) => p.key));

export interface HaneyPoint {
  id: string;
  lon: number;
  lat: number;
  date?: string;
  depth_in?: number;
  values: Record<string, number>; // param key -> value (only the params present in the row)
}

// wire (contract, snake_case) <-> lib mappers for the BE-light haney endpoints
export const haneyToWire = (p: HaneyPoint): HaneySamplePoint => ({
  point_id: p.id,
  lon: p.lon,
  lat: p.lat,
  sampled_at: p.date ?? null,
  depth_in: p.depth_in ?? null,
  values: p.values,
});
export const haneyFromWire = (w: HaneySamplePoint): HaneyPoint => ({
  id: w.point_id,
  lon: w.lon,
  lat: w.lat,
  date: w.sampled_at ?? undefined,
  depth_in: w.depth_in ?? undefined,
  values: w.values ?? {},
});

const norm = (s: string) => s.toLowerCase().replace(/[^a-z0-9]/g, "");

function numericShare(
  rows: string[][],
  col: number,
  test: (n: number) => boolean,
): number {
  let ok = 0;
  let seen = 0;
  for (const r of rows.slice(0, 25)) {
    const v = (r[col] ?? "").trim();
    if (!v) continue;
    seen++;
    const n = Number(v);
    if (Number.isFinite(n) && test(n)) ok++;
  }
  return seen ? ok / seen : 0;
}

// Auto-match CSV headers → Haney targets (exact alias, then contains-alias; coordinates
// validated against their ranges). Returns { targetKey: columnIndex | null }.
export function autoMatchHaney(
  parsed: ParsedCsv,
): Record<string, number | null> {
  const normed = parsed.headers.map(norm);
  const mapping: Record<string, number | null> = {};
  const taken = new Set<number>();
  for (const t of HANEY_TARGETS) {
    mapping[t.key] = null;
    let idx = normed.findIndex(
      (h, i) => !taken.has(i) && t.aliases.includes(h),
    );
    if (idx === -1)
      idx = normed.findIndex(
        (h, i) =>
          !taken.has(i) &&
          t.aliases.some((a) => a.length >= 3 && h.includes(a)),
      );
    if (idx === -1) continue;
    if (
      t.key === "lat" &&
      numericShare(parsed.rows, idx, (n) => Math.abs(n) <= 90) < 0.5
    )
      continue;
    if (
      t.key === "lon" &&
      numericShare(parsed.rows, idx, (n) => Math.abs(n) <= 180) < 0.5
    )
      continue;
    mapping[t.key] = idx;
    taken.add(idx);
  }
  return mapping;
}

// Build georeferenced Haney points from a parsed CSV + column mapping. Rows with a missing/invalid
// coordinate are SKIPPED + counted (never silently dropped).
export function buildHaneyPoints(
  parsed: ParsedCsv,
  mapping: Record<string, number | null>,
): { points: HaneyPoint[]; skipped: number } {
  const points: HaneyPoint[] = [];
  let skipped = 0;
  const cell = (r: string[], key: string) => {
    const c = mapping[key];
    return c == null ? "" : (r[c] ?? "").trim();
  };
  for (const r of parsed.rows) {
    const rawLat = cell(r, "lat");
    const rawLon = cell(r, "lon");
    const lat = rawLat ? Number(rawLat) : NaN;
    const lon = rawLon ? Number(rawLon) : NaN;
    if (
      !Number.isFinite(lat) ||
      !Number.isFinite(lon) ||
      Math.abs(lat) > 90 ||
      Math.abs(lon) > 180
    ) {
      skipped++;
      continue;
    }
    const values: Record<string, number> = {};
    for (const key of PARAM_KEYS) {
      const raw = cell(r, key);
      if (!raw) continue;
      const n = Number(raw);
      if (Number.isFinite(n)) values[key] = n;
    }
    // derive organic P when the report gives only total + inorganic (common Haney layout)
    if (
      values.organic_p == null &&
      Number.isFinite(values.total_p) &&
      Number.isFinite(values.inorganic_p)
    ) {
      values.organic_p = Math.max(0, values.total_p - values.inorganic_p);
    }
    const depthRaw = cell(r, "depth_in");
    points.push({
      id: cell(r, "id") || `H-${points.length + 1}`,
      lon,
      lat,
      date: cell(r, "date") || undefined,
      depth_in:
        depthRaw && Number.isFinite(Number(depthRaw))
          ? Number(depthRaw)
          : undefined,
      values,
    });
  }
  return { points, skipped };
}

// Which Haney params actually have data across the point set (≥1 numeric value) — the params
// we can krige + list.
export function haneyParamsWithData(points: HaneyPoint[]): HaneyParam[] {
  return HANEY_PARAMS.filter((p) =>
    points.some((pt) => Number.isFinite(pt.values[p.key])),
  );
}

// The sidebar search matches the visible label, or the param key as stored.
export function haneyParamsMatching(
  params: HaneyParam[],
  query: string,
): HaneyParam[] {
  const qq = query.trim().toLowerCase();
  if (!qq) return params;
  return params.filter(
    (r) => r.label.toLowerCase().includes(qq) || r.key.includes(qq),
  );
}

export const haneyLayerId = (key: string) => `haney-${key}`;

// Krige ONE Haney parameter onto the field cell grid → a gridded value-colored ManifestLayer.
// The server runs the same local-neighborhood ordinary kriging, so the colours are that surface.
export async function buildHaneyLayer(
  param: HaneyParam,
  points: HaneyPoint[],
  geoms: { type: string; coordinates: unknown }[],
  centroids: { lng: number; lat: number }[],
  contentVersion = "client",
): Promise<ManifestLayer> {
  const krigePts: HaneyKrigePoint[] = points
    .filter((p) => Number.isFinite(p.values[param.key]))
    .map((p) => ({ lng: p.lon, lat: p.lat, value: p.values[param.key] }));
  const values = await krigeHaney(krigePts, centroids);
  const features: unknown[] = [];
  for (let i = 0; i < geoms.length; i++) {
    const v = values[i];
    if (v == null || !Number.isFinite(v)) continue;
    features.push({
      type: "Feature",
      properties: { value: v },
      geometry: geoms[i],
    });
  }
  const present = values.filter(
    (v): v is number => v != null && Number.isFinite(v),
  );
  const min = present.length ? Math.min(...present) : 0;
  const max = present.length ? Math.max(...present) : 1;
  return {
    id: haneyLayerId(param.key),
    kind: "geojson",
    title: `${param.label} (Haney)`,
    inline: { type: "FeatureCollection", features },
    unit: param.unit,
    colormap: "viridis",
    value_min: Math.floor(min),
    value_max: Math.ceil(max > min ? max : min + 1),
    default_visible: true,
    default_opacity: 0.85,
    order: 55,
    content_version: contentVersion,
    is_stale: false,
  };
}

// Orchestrator: fetch the field's cell grid and krige one Haney param onto it → a map layer.
// Haney stays in the browser. Soil composition display layers read stack columns.
export async function haneyKrigedLayer(
  fieldId: string,
  param: HaneyParam,
  points: HaneyPoint[],
): Promise<ManifestLayer> {
  if (!points.some((p) => Number.isFinite(p.values[param.key]))) {
    throw new Error(`No "${param.label}" values in the imported Haney points.`);
  }
  const { geoms, centroids } = await stackCellData(fieldId, []);
  return await buildHaneyLayer(param, points, geoms, centroids);
}
